<template>
  <div class="qr-header">
    <div class="qr-header__row">
      <div class="qr-header__intro">
        <h1 class="qr-header__title">QR Booker Analytics</h1>
        <p class="qr-header__subtitle">
          QR insights · {{ formattedRange }}
        </p>
      </div>

      <div class="qr-header__controls">
        <div class="qr-header__dates">
          <div class="qr-header__date-field">
            <feather-icon icon="CalendarIcon" svgClasses="qr-header__date-icon" />
            <datepicker
              :value="dateFrom"
              :disabled-dates="disabledDatesFrom"
              format="dd MMM yyyy"
              input-class="qr-header__date-input"
              placeholder="Start date"
              @selected="onStartDateSelected"
            />
          </div>
          <feather-icon icon="ArrowRightIcon" svgClasses="qr-header__date-arrow" />
          <div class="qr-header__date-field">
            <feather-icon icon="CalendarIcon" svgClasses="qr-header__date-icon" />
            <datepicker
              :value="dateTo"
              :disabled-dates="disabledDatesTo"
              format="dd MMM yyyy"
              input-class="qr-header__date-input"
              placeholder="End date"
              @selected="onEndDateSelected"
            />
          </div>
        </div>

        <div class="qr-header__select-wrap">
          <select
            class="qr-header__select"
            :value="companyId"
            @change="$emit('update:companyId', $event.target.value)"
          >
            <option
              v-for="company in companyOptions"
              :key="company.id"
              :value="company.id"
            >
              {{ company.name }}
            </option>
          </select>
          <feather-icon icon="ChevronDownIcon" svgClasses="qr-header__select-icon" />
        </div>

        <button
          type="button"
          class="qr-header__btn qr-header__btn--search"
          :disabled="loading"
          @click="$emit('search')"
        >
          <feather-icon icon="SearchIcon" svgClasses="qr-header__btn-icon" />
          {{ loading ? "Searching..." : "Search" }}
        </button>

        <button
          type="button"
          class="qr-header__btn qr-header__btn--download"
          @click="$emit('download')"
        >
          <feather-icon
            icon="DownloadIcon"
            svgClasses="qr-header__btn-icon qr-header__btn-icon--purple"
          />
          Download
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { mapGetters } from "vuex";

export default {
  name: "QrAnalyticsHeader",
  components: { Datepicker },
  props: {
    dateFrom: { type: Date, required: true },
    dateTo: { type: Date, required: true },
    companyId: { type: [String, Number], default: "all" },
    loading: { type: Boolean, default: false },
  },
  computed: {
    ...mapGetters("company", {
      storeCompanies: "getCompanies",
    }),
    companyOptions() {
      const list = (this.storeCompanies || []).map((company) => ({
        id: company.id,
        name: company.name,
      }));
      return [{ id: "all", name: "All Companies" }, ...list];
    },
    formattedRange() {
      const opts = { day: "numeric", month: "short", year: "numeric" };
      const from = this.dateFrom
        ? this.dateFrom.toLocaleDateString("en-GB", opts)
        : "";
      const to = this.dateTo
        ? this.dateTo.toLocaleDateString("en-GB", opts)
        : "";
      return `${from} - ${to}`;
    },
    disabledDatesFrom() {
      const end = this.startOfDay(this.dateTo);
      const today = this.startOfDay(new Date());
      return {
        customPredictor: (date) => {
          const day = this.startOfDay(date);
          if (day > today) return true;
          if (end && day > end) return true;
          return false;
        },
      };
    },
    disabledDatesTo() {
      const start = this.startOfDay(this.dateFrom);
      const today = this.startOfDay(new Date());
      return {
        customPredictor: (date) => {
          const day = this.startOfDay(date);
          if (day > today) return true;
          if (start && day < start) return true;
          return false;
        },
      };
    },
  },
  methods: {
    startOfDay(date) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    isBefore(dateA, dateB) {
      return this.startOfDay(dateA) < this.startOfDay(dateB);
    },
    onStartDateSelected(date) {
      if (!date) return;
      this.$emit("update:dateFrom", date);
      if (this.dateTo && this.isBefore(this.dateTo, date)) {
        this.$emit("update:dateTo", new Date(date));
      }
    },
    onEndDateSelected(date) {
      if (!date) return;
      if (this.dateFrom && this.isBefore(date, this.dateFrom)) {
        this.$emit("update:dateTo", new Date(this.dateFrom));
        return;
      }
      this.$emit("update:dateTo", date);
    },
  },
};
</script>

<style scoped lang="scss">
.qr-header {
  margin-bottom: 1.75rem;

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    flex-wrap: wrap;
  }

  &__intro {
    flex: 1 1 280px;
    min-width: 0;
  }

  &__title {
    font-size: 2rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.35rem;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: #a8a8b3;
    margin: 0;
    line-height: 1.4;
  }

  &__controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.65rem;
    flex: 0 1 auto;
    justify-content: flex-end;
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__date-field {
    position: relative;
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #e4e4e8;
    border-radius: 10px;
    padding: 0 0.75rem 0 0.65rem;
    min-height: 42px;
    min-width: 148px;

    &:focus-within {
      border-color: #c8c4f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.08);
    }

    :deep(.vdp-datepicker) {
      width: 100%;
      min-width: 0;

      input {
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0.55rem 0 !important;
        font-size: 0.875rem !important;
        color: #5e5873 !important;
        width: 100%;
        min-width: 0;
        cursor: pointer;
      }
    }
  }

  &__date-icon {
    width: 16px;
    height: 16px;
    color: #b9b9c3;
    flex-shrink: 0;
    margin-right: 0.4rem;
  }

  &__date-arrow {
    width: 16px;
    height: 16px;
    color: #c4c4cc;
    flex-shrink: 0;
  }

  &__select-wrap {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 42px;
  }

  &__select {
    appearance: none;
    background: #fff;
    border: 1px solid #e4e4e8;
    border-radius: 10px;
    min-height: 42px;
    padding: 0 2.1rem 0 0.9rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #5e5873;
    cursor: pointer;
    min-width: 150px;

    &:focus {
      outline: none;
      border-color: #c8c4f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.08);
    }
  }

  &__select-icon {
    position: absolute;
    right: 0.7rem;
    width: 14px;
    height: 14px;
    color: #b9b9c3;
    pointer-events: none;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    border: none;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 500;
    min-height: 42px;
    padding: 0 1.1rem;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }
  }

  &__btn-icon {
    width: 16px;
    height: 16px;
    color: #fff;

    &--purple {
      color: #7367f0;
    }
  }

  &__btn--search {
    background: #7367f0;
    color: #fff;
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.28);

    &:hover:not(:disabled) {
      background: #685dd8;
    }
  }

  &__btn--download {
    background: #fff;
    color: #7367f0;
    border: 1px solid #e4e4e8;

    &:hover {
      border-color: #d0cce8;
      background: #faf9ff;
    }
  }
}

@media (max-width: 1100px) {
  .qr-header__controls {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .qr-header__dates {
    width: 100%;
    flex-wrap: wrap;
  }

  .qr-header__date-field {
    flex: 1 1 130px;
  }

  .qr-header__btn {
    flex: 1 1 auto;
  }
}
</style>

<style lang="scss">
.qr-header .vdp-datepicker__calendar {
  border-radius: 10px;
  border: 1px solid #e4e4e8;
  box-shadow: 0 8px 24px rgba(34, 41, 47, 0.1);
}
</style>
